import { History } from "history";
import { computed, observable } from "mobx";
import React from "react";
import { FormattedMessage } from "react-intl";

import { Auth0, handleError, UserMetadata } from "nvent-web/services/Auth0";
import * as logger from "nvent-web/services/logger";
import { ProfileUpdate } from "nvent-web/services/resources/ProfileResource";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { UserStore } from "nvent-web/stores/User";
import { CompanyAddress } from "nvent-web/types/Profile";

export interface CredentialsProps {
  email: string;
  password: string;
}

export interface PersonalProps {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export interface CompanyProps {
  companyName: string;
  companyAddress: CompanyAddress;
  consents: Consents;
}

export interface Consents {
  termsOfService: boolean;
  newsletter: boolean;
}

export class RegistrationFlow implements CredentialsProps, PersonalProps, CompanyProps {
  @computed
  get isSigningUp() {
    return !this.auth0.isAuthenticated;
  }

  @observable
  email = this.user.email;

  @observable
  password = "";

  @observable
  firstName = this.user.firstName;

  @observable
  lastName = this.user.lastName;

  @observable
  phoneNumber = this.user.phoneNumber;

  @observable
  companyName = this.user.companyName;

  @observable
  companyAddress: CompanyAddress = { ...this.user.companyAddress };

  @observable
  consents: Consents = {
    termsOfService: false,
    newsletter: false,
  };

  constructor(
    private user: UserStore,
    private auth0: Auth0,
    private history: History,
    private notifications: NotificationsStore,
    private path: string
  ) {}

  credentialsSubmitted = async (credentials: CredentialsProps) => {
    Object.assign(this, credentials);
    this.history.push(`${this.path}/personal`);
  };

  personalSubmitted = async (personal: PersonalProps) => {
    Object.assign(this, personal);
    this.history.push(`${this.path}/company`);
  };

  companySubmitted = async (company: CompanyProps) => {
    Object.assign(this, company);

    if (this.isSigningUp) {
      await this.signUp();
    } else {
      await this.updateProfile();
    }
  };

  registrationConfirmed = async () => {
    // auth0.login will cause a redirect, so we'll need to finish once we return with a token
    this.user.scheduleProfileUpdate(this.profile);

    try {
      await this.auth0.login(this.email, this.password);
    } catch (err) {
      logger.error(err);
      this.notifications.createGenericError(err, "Login error");
      throw err;
    }
  };

  private get profile(): ProfileUpdate {
    return {
      locale: this.user.locale,
      companyName: this.companyName,
      companyAddress: this.companyAddress,
    };
  }

  private get auth0Metadata(): UserMetadata {
    return {
      givenName: this.firstName,
      familyName: this.lastName,
      phoneNumber: this.phoneNumber,
    };
  }

  private async signUp() {
    try {
      await this.auth0.signup(this.email, this.password, this.auth0Metadata);
      this.history.push(`${this.path}/confirmation`);
    } catch (error) {
      return handleError(
        error,
        {
          user_exists: () => {
            this.showError("register.error.userExists");
            this.history.push(this.path);
          },
        },
        (err) => {
          logger.error(err);
          this.notifications.createGenericError(err, "Registration error");
          throw err;
        }
      );
    }
  }

  private async updateProfile() {
    try {
      await this.user.updateProfile(this.profile, this.auth0Metadata);
      this.history.push(this.user.isEmailVerified ? "/" : `${this.path}/confirmation`);
    } catch (err) {
      logger.error(err);
      this.notifications.createGenericError(err, "Registration error");
      throw err;
    }
  }

  private showError(id: string) {
    this.notifications.createError(<FormattedMessage id={id} />);
  }
}
